import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Badge, Button, Input } from 'reactstrap';
import * as Icon from 'react-feather';
import InputModulos from './InputModulos';
import ModalConvocatorias from '../../../layouts/modals/ModalConvocatorias';
import ModalNotasRA from '../../../layouts/modals/ModalNotasRA';
import ModalOptions from '../../../layouts/modals/ModalOptions';
import { AuthContext } from '../../../context/authContext';
import matriculacionApiService from '../../../services/http/matriculacionApiService';
import { getItem, STORAGE_KEYS } from '../../../services/storage';
import useSoloLecturaMatriculas from '../../../hooks/useSoloLecturaMatriculas';
import config from '../../../config';
import { convocatoriasAgotadas } from '../../../helpers/convocatorias';

// Estilos de la sub-tabla de notas de RA. Padding holgado + separadores sutiles
// y alineacion superior para las descripciones largas (multi-linea).
const raTablaStyle = { width: '100%', borderCollapse: 'collapse' };
const raThStyle = {
  padding: '8px 12px',
  textAlign: 'left',
  borderBottom: '2px solid rgba(0, 0, 0, 0.1)',
  fontWeight: 600,
  whiteSpace: 'nowrap',
};
const raTdStyle = {
  padding: '8px 12px',
  verticalAlign: 'top',
  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
};

// Leyes educativas que evaluan por RA (curriculum nuevo LOE 2425/2526). Coincide
// con el discriminador que usa el backend en actas/documentos. SOLO las matriculas
// de estas leyes llaman a los endpoints de notas de RA; el resto no.
const LEYES_CON_RA = ['LOE-2425', 'LOE-2526'];

const Actividad = ({
  idMatricula,
  obj,
  activityStateList,
  convoStateList,
  hoursCounter,
  setHoursCounter,
  total,
  setTotal,
  setTotalSuelto,
  totalSuelto,
  total2,
  setTotal2,
  setTotalSuelto2,
  totalSuelto2,
  modulos,
  setModulos,
  markModulosDirty,
  estado,
  dataApi,
  act,
  setActivity,
}) => {
  const localEnrollment = getItem(STORAGE_KEYS.ENROLLMENT);
  const noMatriculado = obj.matriculal_id === null;
  const { userOU } = useContext(AuthContext);
  const { idCedesca, idCntec } = userOU;
  const soloLectura = useSoloLecturaMatriculas();
  const [modalConvoAbierta, setModalConvoAbierta] = useState(false);
  const [selectedState, setSelectedState] = useState(obj.idEstado);
  const [convoList, setConvoList] = useState([]);
  const [convoVisible, setConvoVisible] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [raw2, setRaw2] = useState('');

  // --- Notas de RA (curriculum nuevo LOE) ---
  const [notasRA, setNotasRA] = useState([]);
  const [catalogoRAs, setCatalogoRAs] = useState([]);
  const [raVisible, setRaVisible] = useState(false);
  const [modalRAAbierta, setModalRAAbierta] = useState(false);
  const [showModalRA, setShowModalRA] = useState(false);
  const [idNotaRABorrar, setIdNotaRABorrar] = useState(null);
  const [refreshRA, setRefreshRA] = useState(0);

  // Solo los perfiles centrales/sede (no delegacion ni orientacion) pueden
  // añadir/eliminar RAs; el resto solo visualiza (misma regla que convocatorias).
  const puedeEditarRA = !((idCedesca > 0 && idCntec > 0) || userOU.centro === 'ORIENTACIÓN');

  // La matricula entera comparte ley educativa; el detalle (dataApi, de
  // detalleMatricula) la trae por fila. Solo el curriculum nuevo evalua por RA:
  // si no lo es, NO se llama a los endpoints de RA ni se pintan sus controles.
  const detalleMatricula = Array.isArray(dataApi) ? dataApi[0] : dataApi;
  const esNuevoCurriculum = LEYES_CON_RA.includes(detalleMatricula?.leyeducativa);

  // Peticiones a la API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const dataConvocatorias = await matriculacionApiService.obtenerConvocatorias({
          headquarter: localEnrollment.headquarter,
          idMatricula,
          idActividad: obj.codigo,
        });
        setConvoList(dataConvocatorias);
      } catch (error) {
        setConvoList([]);
      }
    };

    fetchData();
  }, [idMatricula, localEnrollment?.headquarter, obj?.codigo]);

  // Notas de RA + catalogo del modulo. Solo se pide para matriculas del
  // curriculum nuevo (LOE 2425/2526); en el resto ni siquiera se llama al backend.
  useEffect(() => {
    if (!esNuevoCurriculum) {
      setNotasRA([]);
      setCatalogoRAs([]);
      return;
    }

    const fetchRA = async () => {
      try {
        const data = await matriculacionApiService.obtenerNotasRA({
          headquarter: localEnrollment.headquarter,
          idMatricula,
          idActividad: obj.codigo,
        });
        setNotasRA(Array.isArray(data?.notas) ? data.notas : []);
        setCatalogoRAs(Array.isArray(data?.catalogoRAs) ? data.catalogoRAs : []);
      } catch (error) {
        setNotasRA([]);
        setCatalogoRAs([]);
      }
    };

    fetchRA();
  }, [idMatricula, localEnrollment?.headquarter, obj?.codigo, refreshRA, esNuevoCurriculum]);

  const handleTogleConvo = () => {
    if (obj.convocatorias > 0) {
      if (idCedesca <= 0 && idCntec <= 0) {
        setConvoVisible(!convoVisible);
      }
    }
  };

  const handleActivityState = (e) => {
    const nuevoEstado = parseInt(e.target.value, 10);
    if (nuevoEstado !== obj.idEstado) {
      if (markModulosDirty) markModulosDirty();
      setSelectedState(nuevoEstado);
      // `obj` es la misma referencia que vive dentro de `act` (Desplegable mapea
      // act[keyArray]) y que valida Acciones: mutarlo actualiza el idEstado que
      // lee la validacion. Antes se buscaba con act[obj.modulo], que es undefined
      // cuando el grupo NO se indexa por el modulo (p.ej. Sintesis S2) -> reventaba
      // y el estado no se guardaba pese a verse elegido en el select.
      obj.idEstado = nuevoEstado;
      setActivity(act);
    }
  };

  const handleAddConvo = () => {
    setModalConvoAbierta(true);
  };
  const handleDelete = (id) => {
    setRaw2({
      idConvocatoria: id,
      headquarter: localEnrollment.headquarter,
      numMatricula: idMatricula,
      descripcion: obj.descripcion,
      nombre: obj.nombre,
    });

    setShowModal(true);
  };

  const handleToggleRA = () => {
    setRaVisible(!raVisible);
  };

  const handleAddRA = () => {
    setModalRAAbierta(true);
  };

  const handleDeleteRA = (id) => {
    setIdNotaRABorrar(id);
    setShowModalRA(true);
  };

  // Borra la nota de RA y refresca localmente (sin recargar la pagina, para no
  // perder cambios de estado sin guardar en el resto de la matricula).
  const confirmarBorrarRA = async () => {
    try {
      await matriculacionApiService.eliminarNotaRA({
        headquarter: localEnrollment.headquarter,
        id: idNotaRABorrar,
      });
    } catch (error) {
      // Silencioso: el backend responde deleted: 0 si ya no existia (exito).
    }
    setShowModalRA(false);
    setIdNotaRABorrar(null);
    setRefreshRA((v) => v + 1);
  };

  return (
    <>
      {showModal && (
        <ModalOptions
          header="¿Desea eliminar la convocatoria?"
          url={`${config.REACT_APP_NODE_API}eliminarConvocatoria`}
          raw={raw2}
          load={setShowModal}
          text1="Cancelar"
          color1="danger"
          text2="Aceptar"
          color2="primary"
        />
      )}
      <ModalConvocatorias
        state={modalConvoAbierta}
        setState={setModalConvoAbierta}
        convoStateList={convoStateList}
        idActividad={obj.matriculal_id}
        idGrupo={obj.CodigoGrupo}
        numMatricula={idMatricula}
      />
      {showModalRA && (
        <ModalOptions
          header="¿Desea eliminar la nota de RA?"
          load={setShowModalRA}
          text1="Cancelar"
          color1="danger"
          text2="Aceptar"
          color2="primary"
          onAccept={confirmarBorrarRA}
        />
      )}
      <ModalNotasRA
        state={modalRAAbierta}
        setState={setModalRAAbierta}
        catalogoRAs={catalogoRAs}
        idmatriculal={obj.matriculal_id}
        headquarter={localEnrollment.headquarter}
        onSaved={() => setRefreshRA((v) => v + 1)}
      />
      <tr>
        <td
          style={{ width: '20%' }}
          className={convocatoriasAgotadas(obj) || obj.Nota === 1 ? 'text-black-50' : ''}
          id={obj.codigo}
        >
          <InputModulos
            hoursCounter={hoursCounter}
            setHoursCounter={setHoursCounter}
            total={total}
            totalSuelto={totalSuelto}
            setTotalSuelto={setTotalSuelto}
            setTotal={setTotal}
            total2={total2}
            totalSuelto2={totalSuelto2}
            setTotalSuelto2={setTotalSuelto2}
            setTotal2={setTotal2}
            obj={obj}
            modulos={modulos}
            setModulos={setModulos}
            markModulosDirty={markModulosDirty}
            estado={estado}
            dataApi={dataApi}
          />{' '}
          {obj.codigo}
          {obj.Nota === 1 ? ' - Completado' : ''}
          {convocatoriasAgotadas(obj) ? ' - Superado el máximo de convocatorias' : ''}
        </td>
        <td
          style={{ width: '40%' }}
          className={convocatoriasAgotadas(obj) || obj.Nota === 1 ? 'text-black-50' : ''}
        >
          {obj.descripcion}
        </td>
        <td
          style={{ width: '10%' }}
          className={convocatoriasAgotadas(obj) || obj.Nota === 1 ? 'text-black-50' : ''}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            {(idCedesca > 0 && idCntec > 0) || userOU.centro === 'ORIENTACIÓN' ? (
              <Badge
                color="primary"
                pill
                style={{
                  cursor:
                    obj.convocatorias > 0 && idCedesca <= 0 && idCntec <= 0 ? 'pointer' : 'cursor',
                  marginLeft: '40%',
                  display: 'inline-flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
                onClick={handleTogleConvo}
              >
                {obj.convocatorias}
              </Badge>
            ) : (
              <Badge
                color="primary"
                pill
                style={{
                  cursor:
                    obj.convocatorias > 0 && idCedesca <= 0 && idCntec <= 0 ? 'pointer' : 'cursor',
                  marginRight: '8px',
                  display: 'inline-flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
                onClick={handleTogleConvo}
              >
                {obj.convocatorias}
              </Badge>
            )}

            {(idCedesca > 0 && idCntec > 0) || userOU.centro === 'ORIENTACIÓN' ? (
              ''
            ) : (
              <Button size="sm" onClick={handleAddConvo} disabled={noMatriculado || soloLectura}>
                <Icon.Plus />
              </Button>
            )}
          </div>
          {catalogoRAs.length > 0 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '6px',
              }}
            >
              <Badge
                color="info"
                pill
                style={{
                  cursor: 'pointer',
                  display: 'inline-flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
                onClick={handleToggleRA}
                title="Ver notas de RA"
              >
                RA {notasRA.length}
              </Badge>
              {puedeEditarRA && (
                <Button
                  size="sm"
                  color="info"
                  onClick={handleAddRA}
                  disabled={noMatriculado || soloLectura}
                  title="Añadir nota de RA"
                >
                  <Icon.Plus />
                </Button>
              )}
            </div>
          )}
        </td>
        <td
          style={{ width: '5%', verticalAlign: 'middle' }}
          className={convocatoriasAgotadas(obj) || obj.Nota === 1 ? 'text-black-50' : ''}
        >
          {obj.horas}
        </td>
        <td
          style={{ width: '7%', verticalAlign: 'middle' }}
          className={convocatoriasAgotadas(obj) || obj.Nota === 1 ? 'text-black-50' : ''}
        >
          {obj.precio}€
        </td>
        <td
          style={{ width: '7%', verticalAlign: 'middle' }}
          className={convocatoriasAgotadas(obj) || obj.Nota === 1 ? 'text-black-50' : ''}
        >
          {obj.importe_suelto}€ {/* Nueva columna */}
        </td>
        <td
          style={{ width: '18%' }}
          className={convocatoriasAgotadas(obj) || obj.Nota === 1 ? 'text-black-50' : ''}
        >
          <div style={{ display: 'flex', gap: '8px' }}>
            <Input
              bsSize="sm"
              type="select"
              onChange={handleActivityState}
              disabled={
                (idCedesca > 0 && idCntec > 0) || userOU.centro === 'ORIENTACIÓN' || soloLectura
              }
              value={selectedState}
            >
              {activityStateList.map((activity) => (
                <option key={activity.Id} value={activity.Id}>
                  {activity.Valor}
                </option>
              ))}
            </Input>
          </div>
        </td>
      </tr>
      {convoVisible && (
        <tr>
          <td colSpan="6">
            <Alert color="primary">
              <table style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Descripción</th>
                    <th>Nota</th>
                    <th>Estado</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {convoList.map((c) => (
                    <tr key={c.id}>
                      <td>{c.descripcion}</td>
                      <td>{c.nota}</td>
                      <td>{c.Estado}</td>
                      <td>
                        <Button
                          color="link"
                          onClick={() => handleDelete(c.id)}
                          disabled={
                            noMatriculado ||
                            (idCedesca > 0 && idCntec > 0) ||
                            userOU.centro === 'ORIENTACIÓN' ||
                            soloLectura
                          }
                        >
                          <Icon.Trash />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Alert>
          </td>
        </tr>
      )}
      {raVisible && (
        <tr>
          <td colSpan="7">
            <Alert color="info" className="mb-0">
              <table style={raTablaStyle}>
                <thead>
                  <tr>
                    <th style={{ ...raThStyle, width: '13%' }}>RA</th>
                    <th style={raThStyle}>Descripción</th>
                    <th style={{ ...raThStyle, width: '7%', textAlign: 'center' }}>Nota</th>
                    <th style={{ ...raThStyle, width: '14%' }}>Convocatoria</th>
                    <th style={{ ...raThStyle, width: '48px' }} aria-label="Acciones" />
                  </tr>
                </thead>
                <tbody>
                  {notasRA.length === 0 ? (
                    <tr>
                      <td style={{ ...raTdStyle, textAlign: 'center' }} colSpan="5">
                        Sin notas de RA
                      </td>
                    </tr>
                  ) : (
                    notasRA.map((n) => (
                      <tr key={n.ID}>
                        <td style={{ ...raTdStyle, whiteSpace: 'nowrap', fontWeight: 500 }}>
                          {n.CodiRA}
                        </td>
                        <td style={{ ...raTdStyle, lineHeight: 1.4 }}>
                          {n.desc_cast || n.desc_cat}
                        </td>
                        <td style={{ ...raTdStyle, textAlign: 'center' }}>{n.notaRA}</td>
                        <td style={{ ...raTdStyle, whiteSpace: 'nowrap' }}>
                          {Number(n.conv) === 1 ? 'Ordinaria' : 'Extraordinaria'}
                        </td>
                        <td style={{ ...raTdStyle, textAlign: 'center' }}>
                          {puedeEditarRA && (
                            <Button
                              color="link"
                              className="p-0"
                              onClick={() => handleDeleteRA(n.ID)}
                              disabled={noMatriculado || soloLectura}
                            >
                              <Icon.Trash />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </Alert>
          </td>
        </tr>
      )}
    </>
  );
};

Actividad.propTypes = {
  idMatricula: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  obj: PropTypes.object,
  activityStateList: PropTypes.array,
  convoStateList: PropTypes.array,
  hoursCounter: PropTypes.number,
  setHoursCounter: PropTypes.func,
  total: PropTypes.number,
  setTotal: PropTypes.func,
  modulos: PropTypes.array,
  setModulos: PropTypes.func,
  markModulosDirty: PropTypes.func,
  estado: PropTypes.bool,
  dataApi: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  act: PropTypes.object,
  setActivity: PropTypes.func,
  setTotalSuelto: PropTypes.func,
  totalSuelto: PropTypes.number,
  setTotal2: PropTypes.func,
  total2: PropTypes.number,
  setTotalSuelto2: PropTypes.func,
  totalSuelto2: PropTypes.number,
};

export default Actividad;
